import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { uploadFile } from "@/lib/upload";
import { toast } from "@/lib/toast";
import { CircleNotch, X, FileArrowUp, FileText } from "@phosphor-icons/react";
import { ContractConsent } from "./ContractConsent";
import type { EnrollFormData, UpdateField } from "./types";

export function StepDocuments({
  form,
  updateField,
}: {
  form: EnrollFormData;
  updateField: UpdateField;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleDocUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadError(null);
    setIsUploading(true);
    try {
      const newDocs: EnrollFormData["documents"] = [];
      for (const file of Array.from(files)) {
        if (file.size > 20 * 1024 * 1024) {
          setUploadError(`${file.name}: maks. 20 MB.`);
          continue;
        }
        const result = await uploadFile(file, "documents");
        const fileType: "pdf" | "image" =
          file.type === "application/pdf" ? "pdf" : "image";
        newDocs.push({ name: file.name, fileUrl: result.fileUrl, fileType });
      }
      updateField("documents", [...form.documents, ...newDocs]);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Greška pri uploadu dokumenta.";
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  }

  function removeDoc(index: number) {
    updateField(
      "documents",
      form.documents.filter((_, i) => i !== index),
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Label>
          Certifikati <span className="text-rose-500">*</span>{" "}
          <span className="text-xs font-normal text-muted-foreground">
            (min. 1 dokument)
          </span>
        </Label>

        <input
          type="file"
          accept=".pdf,image/jpeg,image/png"
          multiple
          onChange={handleDocUpload}
          className="hidden"
          id="doc-upload-input"
        />

        <Label
          htmlFor="doc-upload-input"
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed py-8 transition-colors font-normal",
            isUploading
              ? "pointer-events-none border-border bg-muted/30"
              : "border-border/60 hover:border-primary/40 hover:bg-primary/3",
          )}
        >
          {isUploading ? (
            <CircleNotch className="size-7 animate-spin text-primary/40" />
          ) : (
            <FileArrowUp className="size-7 text-muted-foreground/30" />
          )}
          <p className="mt-2 text-sm font-medium text-muted-foreground">
            {isUploading
              ? "Uploadovanje..."
              : "Kliknite za odabir dokumenata"}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground/60">
            PDF, JPG ili PNG · maks. 20 MB po fajlu
          </p>
        </Label>

        {uploadError && (
          <p className="text-xs text-rose-600">{uploadError}</p>
        )}

        {form.documents.length > 0 && (
          <div className="space-y-2">
            {form.documents.map((doc, i) => (
              <div
                key={`${doc.fileUrl}-${i}`}
                className="flex items-center justify-between rounded-lg border bg-muted/20 px-3 py-2.5"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="size-4 shrink-0 text-primary" />
                  <span className="truncate text-sm text-foreground">
                    {doc.name}
                  </span>
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                    {doc.fileType}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeDoc(i)}
                  className="ml-2 shrink-0 hover:bg-rose-50 hover:text-rose-600"
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border bg-muted/20 p-4">
        <Label className="cursor-pointer items-start gap-3 font-normal">
          <Checkbox
            className="mt-0.5"
            checked={form.agreeToTerms}
            onCheckedChange={(c) => updateField("agreeToTerms", c === true)}
          />
          <span className="text-sm leading-relaxed text-foreground">
            Prihvatam{" "}
            <a
              href="/pages/uslovi-koristenja"
              className="font-medium text-primary hover:underline"
            >
              uslove korištenja
            </a>{" "}
            i{" "}
            <a
              href="/pages/politika-privatnosti"
              className="font-medium text-primary hover:underline"
            >
              politiku privatnosti
            </a>{" "}
            platforme Psihotron.
          </span>
        </Label>
      </div>

      <ContractConsent form={form} updateField={updateField} />
    </div>
  );
}
